import { useState, useEffect } from 'react';
import { useQuery, useInfiniteQuery, useQueries } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { api, type BuildRow, type StorageObject, type StorageFolder } from '../lib/api.js';

type SortKey = 'name' | 'size' | 'modified';
type FolderSize = { bytes: number; objectCount: number; truncated: boolean; loading: boolean };

function copyUri(uri: string): void {
  void navigator.clipboard
    .writeText(uri)
    .then(() => toast.success(`Copied ${uri}`))
    .catch(() => toast.error('Copy failed'));
}
import { usePageTitle } from '../hooks/usePageTitle.js';
import { PageHeader } from '../components/PageHeader.js';
import { EmptyState } from '../components/EmptyState.js';

function humanSize(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = -1;
  do {
    v /= 1024;
    i++;
  } while (v >= 1024 && i < units.length - 1);
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i] ?? 'TB'}`;
}

function fmtDate(s: string | null): string {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/** Best-effort folder→build match: the import-data folder IS the namespace; the
 *  prod bucket suffixes it with "emr", so try that too. */
function matchBuild(builds: BuildRow[], folderName: string): BuildRow | undefined {
  return builds.find((b) => b.namespace === folderName)
    ?? builds.find((b) => `${b.namespace}emr` === folderName);
}

const rowStyle = {
  background: 'var(--bg-tertiary)',
  border: '1px solid var(--glass-border)',
} as const;

export function StoragePage({ bucket, prefix = '' }: { bucket?: string; prefix?: string }) {
  usePageTitle('Storage');
  const navigate = useNavigate();
  const [selected, setSelected] = useState<StorageObject | null>(null);
  // Folder sizes are opt-in (a recursive sum per folder); reset when switching buckets.
  const [showSizes, setShowSizes] = useState(false);
  useEffect(() => setShowSizes(false), [bucket]);
  // Filter + sort the current level (the folders/objects already loaded).
  const [filter, setFilter] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('name');
  useEffect(() => setFilter(''), [bucket, prefix]);

  const bucketsQ = useQuery({ queryKey: ['storage-buckets'], queryFn: () => api.listStorageBuckets() });
  const buildsQ = useQuery({ queryKey: ['builds'], queryFn: () => api.listBuilds() });
  const buckets = bucketsQ.data?.buckets ?? [];
  const builds = buildsQ.data?.builds ?? [];
  const activeBucket = buckets.find((b) => b.name === bucket);

  const listing = useInfiniteQuery({
    queryKey: ['storage-list', bucket, prefix],
    queryFn: ({ pageParam }) => api.listStorage(bucket as string, prefix, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextToken ?? undefined,
    enabled: !!bucket,
  });
  const pages = listing.data?.pages ?? [];
  const folders = pages.flatMap((p) => p.folders);
  const objects = pages.flatMap((p) => p.objects);
  const errorMsg = listing.error instanceof Error ? listing.error.message : null;

  const go = (nextPrefix: string) => void navigate({ to: '/storage', search: { bucket, prefix: nextPrefix } });
  const openBucket = (name: string) => void navigate({ to: '/storage', search: { bucket: name, prefix: '' } });

  // Breadcrumb segments from the prefix ("a/b/" → ["a","b"]).
  const segments = prefix.split('/').filter(Boolean);
  const namespace = segments[0] ?? '';
  const currentBuild = namespace ? matchBuild(builds, namespace) : undefined;

  // Lift per-folder sizes to the parent (only when enabled) so we can display AND
  // sort by them. react-query dedupes by key, so this shares the per-row cache.
  const sizeResults = useQueries({
    queries:
      showSizes && bucket
        ? folders.map((f) => ({
            queryKey: ['storage-folder-size', bucket, f.prefix],
            queryFn: () => api.getStorageFolderSize(bucket, f.prefix),
            staleTime: 5 * 60_000,
          }))
        : [],
  });
  const sizeByPrefix = new Map<string, FolderSize>();
  if (showSizes) {
    folders.forEach((f, i) => {
      const r = sizeResults[i];
      if (r) {
        sizeByPrefix.set(f.prefix, {
          bytes: r.data?.bytes ?? 0,
          objectCount: r.data?.objectCount ?? 0,
          truncated: r.data?.truncated ?? false,
          loading: r.isFetching && !r.data,
        });
      }
    });
  }

  // Filter + sort the loaded level (cheap; ≤ a few hundred entries per page).
  const q = filter.trim().toLowerCase();
  const shownFolders = [...folders]
    .filter((f) => !q || f.name.toLowerCase().includes(q))
    .sort((a, b) => {
      if (sortBy === 'size') {
        const sa = sizeByPrefix.get(a.prefix)?.bytes ?? -1;
        const sb = sizeByPrefix.get(b.prefix)?.bytes ?? -1;
        if (sa !== sb) return sb - sa; // largest first; unknown sizes last
      }
      return a.name.localeCompare(b.name);
    });
  const shownObjects = [...objects]
    .filter((o) => !q || o.name.toLowerCase().includes(q))
    .sort((a, b) => {
      if (sortBy === 'size') return b.size - a.size;
      if (sortBy === 'modified') return (b.lastModified ?? '').localeCompare(a.lastModified ?? '');
      return a.name.localeCompare(b.name);
    });

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Storage"
        title={activeBucket ? activeBucket.label : 'S3 buckets'}
        actions={
          buckets.length > 0 ? (
            <div className="flex items-center gap-2">
              {buckets.map((b) => (
                <button
                  key={b.name}
                  type="button"
                  onClick={() => openBucket(b.name)}
                  className="rounded-xl px-3 py-2 text-sm font-semibold transition-opacity duration-150"
                  style={
                    b.name === bucket
                      ? { background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)', color: '#fff', boxShadow: '0 2px 12px rgba(13,207,184,0.3)' }
                      : { background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--text-secondary)' }
                  }
                >
                  {b.label}
                  {b.sensitive && <span className="ml-1.5" title="Live client PHI — handle with care">🔒</span>}
                </button>
              ))}
            </div>
          ) : undefined
        }
      />

      <section className="flex-1 overflow-y-auto p-6 space-y-4">
        {/* No bucket selected → bucket picker cards */}
        {!bucket && (
          <div className="grid gap-4 sm:grid-cols-2">
            {buckets.map((b) => (
              <button
                key={b.name}
                type="button"
                onClick={() => openBucket(b.name)}
                className="text-left rounded-2xl p-5 transition-opacity duration-150 hover:opacity-90"
                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--glass-border)' }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-2xl">☁</span>
                  <span className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{b.label}</span>
                  {b.sensitive && (
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase" style={{ background: 'var(--accent-red)22', color: 'var(--accent-red)', border: '1px solid var(--accent-red)55' }}>
                      sensitive
                    </span>
                  )}
                </div>
                <div className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>{b.description}</div>
                <div className="mt-3 font-mono text-xs" style={{ color: 'var(--text-dimmed)' }}>s3://{b.name}/</div>
              </button>
            ))}
          </div>
        )}

        {bucket && (
          <>
            {/* Breadcrumb + build cross-link */}
            <div className="flex flex-wrap items-center gap-1.5 text-sm">
              <button type="button" onClick={() => go('')} className="font-mono hover:underline" style={{ color: 'var(--accent-blue)' }}>
                {bucket}
              </button>
              {segments.map((seg, i) => {
                const upto = segments.slice(0, i + 1).join('/') + '/';
                return (
                  <span key={upto} className="flex items-center gap-1.5">
                    <span style={{ color: 'var(--text-dimmed)' }}>/</span>
                    <button type="button" onClick={() => go(upto)} className="font-mono hover:underline" style={{ color: i === segments.length - 1 ? 'var(--text-primary)' : 'var(--accent-blue)' }}>
                      {seg}
                    </button>
                  </span>
                );
              })}
              {currentBuild && (
                <button
                  type="button"
                  onClick={() => void navigate({ to: '/builds/$id', params: { id: currentBuild.id } })}
                  className="ml-2 rounded-full px-2.5 py-0.5 text-xs font-semibold hover:opacity-80"
                  style={{ background: 'var(--accent-purple)22', color: 'var(--accent-purple)', border: '1px solid var(--accent-purple)55' }}
                  title={`Open build "${currentBuild.displayName}"`}
                >
                  ⎔ {currentBuild.displayName} →
                </button>
              )}
            </div>

            {/* Toolbar: filter + sort + size toggle */}
            <div className="flex flex-wrap items-center gap-2">
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter this folder by name…"
                className="min-w-[200px] flex-1 rounded-lg px-3 py-1.5 text-sm"
                style={{ background: 'var(--bg-primary)', border: '1px solid var(--glass-border)', color: 'var(--text-primary)' }}
              />
              {q && (
                <span className="shrink-0 text-xs" style={{ color: 'var(--text-dimmed)' }}>
                  {shownFolders.length + shownObjects.length} of {folders.length + objects.length}
                </span>
              )}
              <select
                value={sortBy}
                onChange={(e) => {
                  const v = e.target.value as SortKey;
                  setSortBy(v);
                  if (v === 'size') setShowSizes(true); // need folder sizes to sort by size
                }}
                className="shrink-0 rounded-lg px-2 py-1.5 text-sm"
                style={{ background: 'var(--bg-primary)', border: '1px solid var(--glass-border)', color: 'var(--text-primary)' }}
                title="Sort this level"
              >
                <option value="name">Name A–Z</option>
                <option value="size">Largest first</option>
                <option value="modified">Recently modified</option>
              </select>
              <button
                type="button"
                onClick={() => setShowSizes((v) => !v)}
                className="shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold"
                style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)', color: showSizes ? 'var(--accent-blue)' : 'var(--text-secondary)' }}
                title="Sum object sizes under each folder (recursive, cached). Opt-in — can be heavy on large buckets."
              >
                {showSizes ? '∑ Hide sizes' : '∑ Calculate sizes'}
              </button>
            </div>

            {activeBucket?.sensitive && (
              <div className="rounded-lg px-3 py-2 text-xs" style={{ background: 'var(--accent-red)14', border: '1px solid var(--accent-red)44', color: 'var(--text-secondary)' }}>
                🔒 Live practice data — read-only. Avoid downloading PHI unless necessary.
              </div>
            )}

            {errorMsg && (
              <div className="rounded-lg px-3 py-3 text-sm" style={{ background: 'var(--accent-red)14', border: '1px solid var(--accent-red)55', color: 'var(--accent-red)' }}>
                {errorMsg}
              </div>
            )}

            {/* Listing */}
            {!errorMsg && listing.isLoading && <div className="text-sm" style={{ color: 'var(--text-dimmed)' }}>Loading…</div>}

            {!errorMsg && !listing.isLoading && folders.length === 0 && objects.length === 0 && (
              <EmptyState icon="☁" title="Empty" description="No folders or objects at this path." />
            )}

            {!errorMsg && !listing.isLoading && q && shownFolders.length === 0 && shownObjects.length === 0 && folders.length + objects.length > 0 && (
              <div className="text-sm" style={{ color: 'var(--text-dimmed)' }}>No matches for “{filter}”.</div>
            )}

            <div className="space-y-1.5">
              {shownFolders.map((f) => (
                <FolderRow
                  key={f.prefix}
                  bucket={bucket}
                  folder={f}
                  build={prefix === '' ? matchBuild(builds, f.name) : undefined}
                  size={showSizes ? sizeByPrefix.get(f.prefix) : undefined}
                  onOpen={() => go(f.prefix)}
                  onOpenBuild={(id) => void navigate({ to: '/builds/$id', params: { id } })}
                />
              ))}

              {shownObjects.map((o) => (
                <div key={o.key} className="flex items-center gap-3 rounded-lg px-3 py-2.5" style={rowStyle}>
                  <button
                    type="button"
                    onClick={() => setSelected(o)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left transition-opacity duration-150 hover:opacity-90"
                  >
                    <span className="text-lg">📄</span>
                    <span className="truncate font-medium" style={{ color: 'var(--text-primary)' }}>{o.name}</span>
                  </button>
                  <span className="shrink-0 font-mono text-xs" style={{ color: 'var(--text-dimmed)' }}>{humanSize(o.size)}</span>
                  <span className="hidden shrink-0 font-mono text-xs sm:inline" style={{ color: 'var(--text-dimmed)' }}>{fmtDate(o.lastModified)}</span>
                  <CopyButton uri={`s3://${bucket}/${o.key}`} />
                </div>
              ))}
            </div>

            {listing.hasNextPage && (
              <button
                type="button"
                onClick={() => void listing.fetchNextPage()}
                disabled={listing.isFetchingNextPage}
                className="rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50"
                style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)', color: 'var(--text-secondary)' }}
              >
                {listing.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            )}
          </>
        )}
      </section>

      {selected && bucket && (
        <ObjectViewer bucket={bucket} object={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function CopyButton({ uri }: { uri: string }) {
  return (
    <button
      type="button"
      onClick={() => copyUri(uri)}
      className="shrink-0 rounded-md px-1.5 py-0.5 text-sm hover:opacity-70"
      style={{ color: 'var(--text-dimmed)' }}
      title={`Copy ${uri}`}
    >
      ⧉
    </button>
  );
}

function FolderRow({
  bucket,
  folder,
  build,
  size,
  onOpen,
  onOpenBuild,
}: {
  bucket: string;
  folder: StorageFolder;
  build: BuildRow | undefined;
  size: FolderSize | undefined;
  onOpen: () => void;
  onOpenBuild: (id: string) => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg px-3 py-2.5" style={rowStyle}>
      <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-3 text-left hover:underline">
        <span className="text-lg">📁</span>
        <span className="truncate font-medium" style={{ color: 'var(--text-primary)' }}>{folder.name}</span>
      </button>
      {size && (
        <span className="shrink-0 font-mono text-xs" style={{ color: 'var(--text-dimmed)' }}>
          {size.loading
            ? '…'
            : `${size.truncated ? '≥ ' : ''}${humanSize(size.bytes)} · ${size.objectCount.toLocaleString()}${size.truncated ? '+' : ''} ${size.objectCount === 1 ? 'file' : 'files'}`}
        </span>
      )}
      <CopyButton uri={`s3://${bucket}/${folder.prefix}`} />
      {build && (
        <button
          type="button"
          onClick={() => onOpenBuild(build.id)}
          className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold hover:opacity-80"
          style={{ background: 'var(--accent-purple)22', color: 'var(--accent-purple)', border: '1px solid var(--accent-purple)55' }}
          title={`Open build "${build.displayName}"`}
        >
          ⎔ build →
        </button>
      )}
    </div>
  );
}

function ObjectViewer({ bucket, object, onClose }: { bucket: string; object: StorageObject; onClose: () => void }) {
  const metaQ = useQuery({
    queryKey: ['storage-object', bucket, object.key],
    queryFn: () => api.getStorageObject(bucket, object.key),
  });
  const meta = metaQ.data;
  const viewUrl = api.storageDownloadUrl(bucket, object.key, 'inline');
  const downloadUrl = api.storageDownloadUrl(bucket, object.key, 'attachment');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6" style={{ background: 'rgba(0,0,0,0.55)' }} onClick={onClose}>
      <div
        className="flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--glass-border)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b px-5 py-4" style={{ borderColor: 'var(--glass-border)' }}>
          <div className="min-w-0">
            <div className="truncate text-base font-bold" style={{ color: 'var(--text-primary)' }}>{object.name}</div>
            <div className="truncate font-mono text-xs" style={{ color: 'var(--text-dimmed)' }}>{object.key}</div>
          </div>
          <button type="button" onClick={onClose} className="shrink-0 text-xl" style={{ color: 'var(--text-dimmed)' }}>✕</button>
        </div>

        <div className="grid grid-cols-2 gap-x-6 gap-y-1 px-5 py-3 text-xs sm:grid-cols-4" style={{ color: 'var(--text-secondary)' }}>
          <div><span style={{ color: 'var(--text-dimmed)' }}>Size</span><br />{humanSize(object.size)}</div>
          <div><span style={{ color: 'var(--text-dimmed)' }}>Type</span><br />{meta?.contentType ?? '…'}</div>
          <div><span style={{ color: 'var(--text-dimmed)' }}>Modified</span><br />{fmtDate(object.lastModified)}</div>
          <div className="truncate"><span style={{ color: 'var(--text-dimmed)' }}>ETag</span><br />{meta?.etag?.replace(/"/g, '') ?? '…'}</div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto border-t px-5 py-4" style={{ borderColor: 'var(--glass-border)' }}>
          {metaQ.isLoading && <div className="text-sm" style={{ color: 'var(--text-dimmed)' }}>Loading…</div>}
          {metaQ.error instanceof Error && (
            <div className="text-sm" style={{ color: 'var(--accent-red)' }}>{metaQ.error.message}</div>
          )}
          {meta?.isImage && <img src={viewUrl} alt={object.name} className="max-h-[55vh] rounded-lg" />}
          {meta?.isPdf && <iframe src={viewUrl} title={object.name} className="h-[55vh] w-full rounded-lg" style={{ background: '#fff' }} />}
          {meta && !meta.isImage && !meta.isPdf && meta.bodyText != null && (
            <pre className="whitespace-pre-wrap break-words font-mono text-xs" style={{ color: 'var(--text-secondary)' }}>
              {meta.bodyText}
              {meta.truncated && <div className="mt-2" style={{ color: 'var(--text-dimmed)' }}>… preview truncated (download for the full object)</div>}
            </pre>
          )}
          {meta && !meta.isImage && !meta.isPdf && meta.bodyText == null && (
            <div className="text-sm" style={{ color: 'var(--text-dimmed)' }}>No inline preview for {meta.contentType}. Use download below.</div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t px-5 py-3" style={{ borderColor: 'var(--glass-border)' }}>
          <button
            type="button"
            onClick={() => copyUri(`s3://${bucket}/${object.key}`)}
            className="rounded-lg px-4 py-2 text-sm font-semibold"
            style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)', color: 'var(--text-secondary)' }}
          >
            Copy s3:// URI
          </button>
          <a href={downloadUrl} className="rounded-lg px-4 py-2 text-sm font-semibold text-white" style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}>
            Download
          </a>
        </div>
      </div>
    </div>
  );
}
